import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import '../../ContactSales.css';

export default function ContactSales() {
    const [calendlyLoaded, setCalendlyLoaded] = useState(false);
    const [activeTab, setActiveTab] = useState('calendly');
    const [calendlyLink, setCalendlyLink] = useState('');
    
    // Phone numbers state
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [salesHotline, setSalesHotline] = useState('');
    const [landline, setLandline] = useState('');
    const [tollFree, setTollFree] = useState('');
    
    // Images stored in public/images/promo folder
    const salesTeamImage = process.env.PUBLIC_URL + '/images/promo/sales-team.png';
    
    useEffect(() => {
        // Load dynamic Calendly link from localStorage or use default
        const storedLink = localStorage.getItem('calendly_link') || process.env.REACT_APP_CALENDLY_LINK || 'https://calendly.com/abdulrahman-timexpress/30min';
        setCalendlyLink(storedLink);
        
        // Load phone numbers from localStorage or use defaults from env
        setWhatsappNumber(localStorage.getItem('whatsapp_number') || process.env.REACT_APP_WHATSAPP_NUMBER || '971552689069');
        setSalesHotline(localStorage.getItem('sales_hotline') || process.env.REACT_APP_SALES_HOTLINE || '971557539410');
        setLandline(localStorage.getItem('landline') || process.env.REACT_APP_LANDLINE || '97142555563');
        setTollFree(localStorage.getItem('toll_free') || process.env.REACT_APP_TOLL_FREE || '600503327');
        
        // Simulate calendly loading
        const timer = setTimeout(() => {
            setCalendlyLoaded(true);
        }, 1500);
        return () => clearTimeout(timer);
    }, []);
    
    // Format phone number for display
    const formatPhone = (num) => {
        if (!num) return '';
        const cleaned = num.replace(/\D/g, '');
        if (cleaned.startsWith('971')) {
            return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
        }
        if (cleaned.startsWith('600')) {
            return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
        }
        return cleaned;
    };

    // Structured data for SEO
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact Time Express Sales",
        "description": "Schedule a call or contact our sales team to get 20-30% off international shipping",
        "url": "https://shiper.io/contact-sales",
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+971-4-299-6888",
            "contactType": "sales",
            "areaServed": "AE",
            "availableLanguage": ["English", "Arabic"]
        },
        "potentialAction": {
            "@type": "ScheduleAction",
            "name": "Book a Sales Call",
            "target": calendlyLink || "https://calendly.com/abdulrahman-timexpress/30min"
        }
    };
    
    return (
        <div className="contact-sales-page">
            <Helmet>
                <title>Contact Sales - Get 20% to 30% OFF International Shipping | Time Express UAE</title>
                <meta name="description" content="Book a call with Time Express sales team. Get up to 30% discount on international courier services from UAE. Choose Starter (20% off) or Pro Plan (30% off). Free consultation available." />
                <meta name="keywords" content="Time Express sales, international shipping discount UAE, courier discount Dubai, shipping savings, bulk shipping rates, business shipping UAE" />
                <link rel="canonical" href="https://shiper.io/contact-sales" />
                
                {/* Open Graph */}
                <meta property="og:title" content="Get 20-30% OFF International Shipping | Contact Time Express Sales" />
                <meta property="og:description" content="Schedule a free consultation with our sales team and unlock exclusive shipping discounts for your business." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://shiper.io/contact-sales" />
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Contact Time Express Sales - Save up to 30% on Shipping" />
                <meta name="twitter:description" content="Book a call to discuss exclusive shipping rates for your business." />
                
                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>

            {/* Hero Section */}
            <section className="sales-hero" aria-label="Contact Sales Hero">
                <div className="container">
                    <div className="hero-badge">
                        <span className="badge-icon">🎯</span>
                        <span>Limited Time Offer</span>
                    </div>
                    <h1>Save Up to <span className="highlight">30% OFF</span> on International Shipping</h1>
                    <p>Join thousands of businesses saving on every shipment. Talk to our experts today!</p>
                    
                    {/* Quick Action Buttons */}
                    <div className="hero-actions">
                        <a href={`tel:+${salesHotline}`} className="hero-btn call-btn">
                            <span className="btn-icon">📞</span>
                            <span className="btn-content">
                                <span className="btn-label">Call Now</span>
                                <span className="btn-sub">{formatPhone(salesHotline)}</span>
                            </span>
                        </a>
                        <a href="#book-call" className="hero-btn book-btn">
                            <span className="btn-icon">📅</span>
                            <span className="btn-content">
                                <span className="btn-label">Book a Call</span>
                                <span className="btn-sub">Schedule Online</span>
                            </span>
                        </a>
                        <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="hero-btn whatsapp-btn">
                            <span className="btn-icon">💬</span>
                            <span className="btn-content">
                                <span className="btn-label">WhatsApp</span>
                                <span className="btn-sub">Chat with Us</span>
                            </span>
                        </a>
                    </div>
                </div>
                
                {/* Animated Background Elements */}
                <div className="hero-shapes">
                    <div className="shape shape-1"></div>
                    <div className="shape shape-2"></div>
                    <div className="shape shape-3"></div>
                </div>
            </section>

            {/* Main Content - Plans & Calendly */}
            <section className="sales-main-section" id="book-call" aria-label="Plans and Booking">
                <div className="container">
                    <div className="sales-layout">
                        {/* Left Side - Plans */}
                        <div className="plans-column">
                            <div className="plans-header">
                                <h2>Choose Your Plan</h2>
                                <p>Select the option that fits your shipping needs</p>
                            </div>
                            
                            {/* Starter Plan */}
                            <article className="plan-card starter" itemScope itemType="https://schema.org/Product">
                                <div className="plan-icon">🚀</div>
                                <div className="plan-content">
                                    <h3 itemProp="name">Starter Plan</h3>
                                    <div className="plan-discount">
                                        <span className="discount-value">20%</span>
                                        <span className="discount-label">OFF</span>
                                    </div>
                                    <ul className="plan-features">
                                        <li>
                                            <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            <span>Top Up <strong>AED 500</strong></span>
                                        </li>
                                        <li>
                                            <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            <span>20% OFF International</span>
                                        </li>
                                        <li>
                                            <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            <span>Ideal for Occasional Shippers</span>
                                        </li>
                                    </ul>
                                </div>
                            </article>

                            {/* Pro Plan */}
                            <article className="plan-card pro" itemScope itemType="https://schema.org/Product">
                                <div className="plan-badge">⭐ Most Popular</div>
                                <div className="plan-icon">💎</div>
                                <div className="plan-content">
                                    <h3 itemProp="name">Pro Plan</h3>
                                    <div className="plan-discount">
                                        <span className="discount-value">30%</span>
                                        <span className="discount-label">OFF</span>
                                    </div>
                                    <ul className="plan-features">
                                        <li>
                                            <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            <span>Top Up <strong>AED 1000+</strong></span>
                                        </li>
                                        <li>
                                            <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            <span>30% OFF International</span>
                                        </li>
                                        <li>
                                            <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            <span>Perfect for Frequent Shippers & SMEs</span>
                                        </li>
                                    </ul>
                                </div>
                            </article>

                            {/* Benefits */}
                            <div className="plan-benefits">
                                <div className="benefit">
                                    <span className="benefit-emoji">🔒</span>
                                    <span>Funds Secure</span>
                                </div>
                                <div className="benefit">
                                    <span className="benefit-emoji">♾️</span>
                                    <span>No Expiry</span>
                                </div>
                                <div className="benefit">
                                    <span className="benefit-emoji">🌍</span>
                                    <span>All Deliveries</span>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Calendly */}
                        <div className="calendly-column">
                            <div className="calendly-widget">
                                {/* Calendly Header */}
                                <div className="calendly-header">
                                    <div className="header-tabs">
                                        <button 
                                            className={`tab-btn ${activeTab === 'calendly' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('calendly')}
                                        >
                                            <span className="tab-icon">📅</span>
                                            <span>Book a Call</span>
                                        </button>
                                        <button 
                                            className={`tab-btn ${activeTab === 'call' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('call')}
                                        >
                                            <span className="tab-icon">📞</span>
                                            <span>Call Now</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Calendly Content */}
                                <div className="calendly-content">
                                    {activeTab === 'calendly' ? (
                                        <>
                                            {/* Loading State */}
                                            {!calendlyLoaded && (
                                                <div className="calendly-loading">
                                                    <div className="loading-spinner">
                                                        <div className="spinner-ring"></div>
                                                        <div className="spinner-ring"></div>
                                                        <div className="spinner-ring"></div>
                                                    </div>
                                                    <p>Loading scheduler...</p>
                                                    <div className="loading-dots">
                                                        <span></span>
                                                        <span></span>
                                                        <span></span>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Calendly Iframe */}
                                            <div className={`calendly-iframe-wrapper ${calendlyLoaded ? 'loaded' : ''}`}>
                                                <iframe 
                                                    src={`${calendlyLink}?embed_domain=shiper.io&embed_type=Inline&hide_gdpr_banner=1&background_color=ffffff&text_color=1f2937&primary_color=7f3f98`} 
                                                    width="100%" 
                                                    height="650" 
                                                    frameBorder="0" 
                                                    title="Schedule a Call with Time Express Sales Team"
                                                    onLoad={() => setCalendlyLoaded(true)}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <div className="call-options">
                                            <div className="call-card">
                                                <div className="call-icon">📞</div>
                                                <h3>Sales Hotline</h3>
                                                <a href={`tel:+${salesHotline}`} className="call-number">{formatPhone(salesHotline)}</a>
                                                <p>Mon - Sat: 9AM - 6PM</p>
                                                <div className="call-action-wrapper">
                                                    <a href={`tel:+${salesHotline}`} className="call-action-btn">
                                                        Call Now
                                                    </a>
                                                </div>
                                            </div>
                                            <div className="call-card">
                                                <div className="call-icon">📱</div>
                                                <h3>Direct Line</h3>
                                                <a href={`tel:+${landline}`} className="call-number">{formatPhone(landline)}</a>
                                                <p>Mon - Sat: 9AM - 6PM</p>
                                                <div className="call-action-wrapper">
                                                    <a href={`tel:+${landline}`} className="call-action-btn">
                                                        Call Now
                                                    </a>
                                                </div>
                                            </div>
                                            <div className="call-card">
                                                <div className="call-icon">🆓</div>
                                                <h3>Toll Free</h3>
                                                <a href={`tel:${tollFree}`} className="call-number">{formatPhone(tollFree)}</a>
                                                <p>Free Call in UAE</p>
                                                <div className="call-action-wrapper">
                                                    <a href={`tel:${tollFree}`} className="call-action-btn toll-free">
                                                        Call Now
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Team Image */}
                            <div className="team-image-container">
                                <img 
                                    src={salesTeamImage} 
                                    alt="Time Express Sales Team Ready to Help" 
                                    className="team-image"
                                    loading="lazy"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                                <div className="team-overlay">
                                    <span className="team-text">Our Experts Are Ready to Help!</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="how-it-works-section" aria-label="How It Works">
                <div className="container">
                    <h2>How It Works</h2>
                    <div className="steps-row">
                        <div className="step-card">
                            <div className="step-number">1</div>
                            <div className="step-icon">💬</div>
                            <h3>Talk to Sales</h3>
                            <p>Book a call or contact us directly</p>
                        </div>
                        <div className="step-arrow">→</div>
                        <div className="step-card">
                            <div className="step-number">2</div>
                            <div className="step-icon">📋</div>
                            <h3>Choose Plan</h3>
                            <p>Select Starter or Pro based on needs</p>
                        </div>
                        <div className="step-arrow">→</div>
                        <div className="step-card">
                            <div className="step-number">3</div>
                            <div className="step-icon">💳</div>
                            <h3>Top Up Wallet</h3>
                            <p>Add funds to your account</p>
                        </div>
                        <div className="step-arrow">→</div>
                        <div className="step-card">
                            <div className="step-number">4</div>
                            <div className="step-icon">🎉</div>
                            <h3>Start Saving</h3>
                            <p>Instant discounts on every shipment!</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="sales-cta-section" aria-label="Call to Action">
                <div className="container">
                    <h2>Ready to Start Saving?</h2>
                    <p>Open a wallet account today and enjoy instant savings on every shipment!</p>
                    <div className="cta-buttons">
                        <a href="/open-a-wallet-account" className="btn-cta primary">
                            Open Wallet Account
                        </a>
                    </div>
                    <div className="cta-phone-numbers">
                        <a href={`tel:+${salesHotline}`} className="cta-phone-link">
                            <span className="phone-icon">📞</span>
                            <span>{formatPhone(salesHotline)}</span>
                        </a>
                        <span className="phone-divider">|</span>
                        <a href={`tel:+${landline}`} className="cta-phone-link">
                            <span className="phone-icon">📱</span>
                            <span>{formatPhone(landline)}</span>
                        </a>
                        <span className="phone-divider">|</span>
                        <a href={`tel:${tollFree}`} className="cta-phone-link">
                            <span className="phone-icon">🆓</span>
                            <span>{formatPhone(tollFree)}</span>
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
